import React, { useEffect, useState } from 'react';
import { getInsights } from '../services/api';

export default function Insights({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getInsights()
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to fetch insights'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  // Very simple Markdown-like rendering
  const renderInsights = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;

      // Bold headers like **Title**
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return (
          <h3 key={i} className="text-base font-bold text-slate-800 mt-4 mb-1">
            {trimmed.replace(/\*\*/g, '')}
          </h3>
        );
      }

      // Headers starting with #
      if (trimmed.startsWith('## ')) {
        return (
          <h3 key={i} className="text-base font-bold text-slate-800 mt-4 mb-1">
            {trimmed.replace(/^##\s*/, '')}
          </h3>
        );
      }
      if (trimmed.startsWith('# ')) {
        return (
          <h2 key={i} className="text-lg font-bold text-slate-800 mt-4 mb-1">
            {trimmed.replace(/^#\s*/, '')}
          </h2>
        );
      }

      // Bullet points
      if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
        const content = trimmed.replace(/^[-•*]\s*/, '');
        return (
          <div key={i} className="flex items-start gap-2 py-0.5 ml-2">
            <span className="mt-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
            <span className="text-sm text-slate-600" dangerouslySetInnerHTML={{
              __html: content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }} />
          </div>
        );
      }

      // Regular paragraph with inline bold
      return (
        <p key={i} className="text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{
          __html: trimmed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-800">$1</strong>')
        }} />
      );
    });
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="skeleton h-8 w-48 rounded" />
        <div className="skeleton h-96 rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">AI-Powered Insights</h2>
        <p className="text-sm text-slate-400 mt-1">
          {data?.total_transactions > 0
            ? `Analysis of ${data.total_transactions} transactions`
            : 'Upload transactions to get personalized advice'}
        </p>
      </div>

      {/* AI badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
          <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
          Powered by Google Gemini AI
        </span>
        <span className="text-xs text-slate-400">{data?.analysis_period}</span>
      </div>

      {/* Insights card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 animate-fade-in-up">
        <div className="prose prose-sm max-w-none">
          {renderInsights(data?.insights)}
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-slate-400 text-center">
        ⚠️ AI insights are for informational purposes only and do not constitute financial advice.
      </p>
    </div>
  );
}
